<script>
import {
  PRE_CHAT_STEP_FIELD_TYPES,
  clonePreChatStep,
  createPreChatElement,
  createPreChatStep,
  isChoiceElement,
} from 'shared/helpers/preChatFormConfig';

const FIELD_LABELS = Object.fromEntries(
  PRE_CHAT_STEP_FIELD_TYPES.map(option => [option.value, option.label])
);

export default {
  name: 'PreChatStepBuilder',
  props: {
    steps: {
      type: Array,
      default: () => [],
    },
    headerImageUrl: {
      type: String,
      default: '',
    },
    headerTitle: {
      type: String,
      default: '',
    },
    headerSubtitle: {
      type: String,
      default: '',
    },
  },
  emits: [
    'update:steps',
    'update:headerTitle',
    'update:headerSubtitle',
    'imageSelected',
    'clearImage',
  ],
  data() {
    return {
      addFieldDrafts: {},
    };
  },
  computed: {
    availableFieldTypes() {
      return PRE_CHAT_STEP_FIELD_TYPES;
    },
  },
  methods: {
    isChoiceElement,
    fieldTypeLabel(type) {
      return FIELD_LABELS[type] || type;
    },
    cloneSteps() {
      return (this.steps || []).map(clonePreChatStep);
    },
    emitSteps(nextSteps) {
      this.$emit('update:steps', nextSteps);
    },
    updateSteps(mutate) {
      const nextSteps = this.cloneSteps();
      mutate(nextSteps);
      this.emitSteps(nextSteps);
    },
    getDraftFieldType(stepId) {
      return this.addFieldDrafts[stepId] || this.availableFieldTypes[0].value;
    },
    setDraftFieldType(stepId, value) {
      this.addFieldDrafts = {
        ...this.addFieldDrafts,
        [stepId]: value,
      };
    },
    addStep() {
      this.updateSteps(nextSteps => {
        nextSteps.push(
          createPreChatStep({
            title: `Page ${nextSteps.length + 1}`,
            description: '',
            elements: [createPreChatElement('information')],
          })
        );
      });
    },
    removeStep(stepId) {
      if ((this.steps || []).length <= 1) return;
      this.updateSteps(nextSteps => {
        const index = nextSteps.findIndex(step => step.id === stepId);
        if (index === -1) return;
        nextSteps.splice(index, 1);
      });
    },
    moveStep(stepId, direction) {
      this.updateSteps(nextSteps => {
        const index = nextSteps.findIndex(step => step.id === stepId);
        const targetIndex = index + direction;
        if (
          index === -1 ||
          targetIndex < 0 ||
          targetIndex >= nextSteps.length
        ) {
          return;
        }
        const [step] = nextSteps.splice(index, 1);
        nextSteps.splice(targetIndex, 0, step);
      });
    },
    updateStepProp(stepId, key, value) {
      this.updateSteps(nextSteps => {
        const step = nextSteps.find(item => item.id === stepId);
        if (!step) return;
        step[key] = value;
      });
    },
    addField(stepId) {
      const type = this.getDraftFieldType(stepId);
      this.updateSteps(nextSteps => {
        const step = nextSteps.find(item => item.id === stepId);
        if (!step) return;
        step.elements = [...(step.elements || []), createPreChatElement(type)];
      });
    },
    removeField(stepId, fieldId) {
      this.updateSteps(nextSteps => {
        const step = nextSteps.find(item => item.id === stepId);
        if (!step) return;
        step.elements = (step.elements || []).filter(
          field => field.id !== fieldId
        );
      });
    },
    moveField(stepId, fieldId, direction) {
      this.updateSteps(nextSteps => {
        const step = nextSteps.find(item => item.id === stepId);
        if (!step) return;
        const index = (step.elements || []).findIndex(
          field => field.id === fieldId
        );
        const targetIndex = index + direction;
        if (
          index === -1 ||
          targetIndex < 0 ||
          targetIndex >= step.elements.length
        ) {
          return;
        }
        const [field] = step.elements.splice(index, 1);
        step.elements.splice(targetIndex, 0, field);
      });
    },
    updateFieldProp(stepId, fieldId, key, value) {
      this.updateSteps(nextSteps => {
        const step = nextSteps.find(item => item.id === stepId);
        const field = step?.elements?.find(item => item.id === fieldId);
        if (!field) return;
        field[key] = value;
      });
    },
    addChoice(stepId, fieldId) {
      this.updateSteps(nextSteps => {
        const step = nextSteps.find(item => item.id === stepId);
        const field = step?.elements?.find(item => item.id === fieldId);
        if (!field) return;
        field.choices = [...(field.choices || []), ''];
      });
    },
    removeChoice(stepId, fieldId, choiceIndex) {
      this.updateSteps(nextSteps => {
        const step = nextSteps.find(item => item.id === stepId);
        const field = step?.elements?.find(item => item.id === fieldId);
        if (!field) return;
        const choices = [...(field.choices || [])];
        choices.splice(choiceIndex, 1);
        field.choices = choices;
      });
    },
    handleImageSelected(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = loadEvent => {
        this.$emit('imageSelected', {
          file,
          url: loadEvent?.target?.result || '',
        });
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    },
  },
};
</script>

<template>
  <div class="spb-root">
    <section class="spb-section">
      <div class="spb-section__label">Step hero</div>
      <div class="spb-hero-card">
        <div class="spb-hero-upload">
          <div class="spb-upload-preview">
            <img v-if="headerImageUrl" :src="headerImageUrl" alt="" />
            <div v-else class="spb-upload-placeholder">Image</div>
          </div>
          <div class="spb-upload-actions">
            <label class="spb-upload-btn">
              Upload image
              <input
                class="spb-hidden-upload"
                type="file"
                accept="image/*"
                @change="handleImageSelected"
              />
            </label>
            <button
              v-if="headerImageUrl"
              type="button"
              class="spb-clear-btn"
              @click="$emit('clearImage')"
            >
              Remove
            </button>
          </div>
        </div>

        <div class="spb-grid">
          <div class="spb-field">
            <label class="spb-field__label">Header title</label>
            <input
              :value="headerTitle"
              type="text"
              class="spb-input"
              placeholder="Hello, I'm Support."
              @input="$emit('update:headerTitle', $event.target.value)"
            />
          </div>
          <div class="spb-field">
            <label class="spb-field__label">Header subtitle</label>
            <textarea
              :value="headerSubtitle"
              rows="3"
              class="spb-textarea"
              placeholder="Tell customers what they can expect before they start."
              @input="$emit('update:headerSubtitle', $event.target.value)"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="spb-section">
      <div class="spb-section__label">Pages</div>
      <div class="spb-step-list">
        <article
          v-for="(step, stepIndex) in steps"
          :key="step.id"
          class="spb-step-card"
        >
          <div class="spb-step-card__head">
            <div>
              <p class="spb-step-card__eyebrow">Page {{ stepIndex + 1 }}</p>
              <h4 class="spb-step-card__title">
                {{ step.title || `Page ${stepIndex + 1}` }}
              </h4>
            </div>

            <div class="spb-step-card__actions">
              <button
                type="button"
                class="spb-icon-btn"
                :disabled="stepIndex === 0"
                @click="moveStep(step.id, -1)"
              >
                ↑
              </button>
              <button
                type="button"
                class="spb-icon-btn"
                :disabled="stepIndex === steps.length - 1"
                @click="moveStep(step.id, 1)"
              >
                ↓
              </button>
              <button
                type="button"
                class="spb-delete-btn"
                :disabled="steps.length <= 1"
                @click="removeStep(step.id)"
              >
                Delete
              </button>
            </div>
          </div>

          <div class="spb-grid">
            <div class="spb-field">
              <label class="spb-field__label">Page title</label>
              <input
                :value="step.title"
                type="text"
                class="spb-input"
                placeholder="May I have your name?"
                @input="updateStepProp(step.id, 'title', $event.target.value)"
              />
            </div>
            <div class="spb-field">
              <label class="spb-field__label">Helper text</label>
              <textarea
                :value="step.description"
                rows="2"
                class="spb-textarea"
                placeholder="Explain what should be filled in on this page."
                @input="
                  updateStepProp(step.id, 'description', $event.target.value)
                "
              />
            </div>
          </div>

          <div class="spb-field-list">
            <div v-if="!(step.elements || []).length" class="spb-empty-state">
              Add at least one field to this page.
            </div>

            <div
              v-for="(field, fieldIndex) in step.elements || []"
              :key="field.id"
              class="spb-field-card"
            >
              <div class="spb-field-card__head">
                <div class="spb-field-card__title">
                  {{ fieldTypeLabel(field.type) }}
                </div>
                <div class="spb-field-card__actions">
                  <label
                    v-if="field.type !== 'information'"
                    class="spb-required-toggle"
                  >
                    <input
                      :checked="!!field.required"
                      type="checkbox"
                      @change="
                        updateFieldProp(
                          step.id,
                          field.id,
                          'required',
                          $event.target.checked
                        )
                      "
                    />
                    <span>Required</span>
                  </label>
                  <button
                    type="button"
                    class="spb-icon-btn"
                    :disabled="fieldIndex === 0"
                    @click="moveField(step.id, field.id, -1)"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    class="spb-icon-btn"
                    :disabled="fieldIndex === (step.elements || []).length - 1"
                    @click="moveField(step.id, field.id, 1)"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    class="spb-delete-btn"
                    @click="removeField(step.id, field.id)"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div v-if="field.type === 'information'" class="spb-field">
                <label class="spb-field__label">Message</label>
                <textarea
                  :value="field.message"
                  rows="3"
                  class="spb-textarea"
                  placeholder="Short helper message for this page"
                  @input="
                    updateFieldProp(
                      step.id,
                      field.id,
                      'message',
                      $event.target.value
                    )
                  "
                />
              </div>

              <div v-else class="spb-field">
                <label class="spb-field__label">Label</label>
                <input
                  :value="field.label"
                  type="text"
                  class="spb-input"
                  placeholder="Question or input label"
                  @input="
                    updateFieldProp(
                      step.id,
                      field.id,
                      'label',
                      $event.target.value
                    )
                  "
                />
              </div>

              <div v-if="isChoiceElement(field.type)" class="spb-choice-list">
                <div
                  v-for="(choice, choiceIndex) in field.choices || []"
                  :key="`${field.id}_${choiceIndex}`"
                  class="spb-choice-row"
                >
                  <input
                    :value="choice"
                    type="text"
                    class="spb-input spb-input--choice"
                    :placeholder="`Option ${choiceIndex + 1}`"
                    @input="
                      updateFieldProp(
                        step.id,
                        field.id,
                        'choices',
                        (field.choices || []).map((item, index) =>
                          index === choiceIndex ? $event.target.value : item
                        )
                      )
                    "
                  />
                  <button
                    v-if="(field.choices || []).length > 1"
                    type="button"
                    class="spb-icon-btn"
                    @click="removeChoice(step.id, field.id, choiceIndex)"
                  >
                    ✕
                  </button>
                </div>
                <button
                  type="button"
                  class="spb-ghost-btn"
                  @click="addChoice(step.id, field.id)"
                >
                  + Add option
                </button>
              </div>
            </div>
          </div>

          <div class="spb-add-field-row">
            <select
              :value="getDraftFieldType(step.id)"
              class="spb-select"
              @change="setDraftFieldType(step.id, $event.target.value)"
            >
              <option
                v-for="option in availableFieldTypes"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <button
              type="button"
              class="spb-ghost-btn"
              @click="addField(step.id)"
            >
              + Add field
            </button>
          </div>
        </article>
      </div>

      <button type="button" class="spb-add-step-btn" @click="addStep">
        + Add page
      </button>
    </section>
  </div>
</template>

<style scoped>
.spb-root {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.spb-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.spb-section__label {
  line-height: 24px;
  color: #111827;
  font-weight: 700;
  font-size: 16px;
}

.spb-hero-card,
.spb-step-card {
  border: 1px solid #dde2e6;
  border-radius: 12px;
  background: #ffffff;
  padding: 16px;
}

.spb-hero-upload {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.spb-upload-preview {
  width: 84px;
  height: 84px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px dashed #bcc6d0;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spb-upload-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.spb-upload-placeholder {
  font-size: 12px;
  color: #94a3b8;
}

.spb-upload-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spb-upload-btn,
.spb-clear-btn,
.spb-ghost-btn,
.spb-add-step-btn,
.spb-icon-btn,
.spb-delete-btn {
  border: 1px solid #bcc6d0;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
}

.spb-upload-btn,
.spb-clear-btn,
.spb-ghost-btn,
.spb-add-step-btn,
.spb-delete-btn {
  height: 36px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.spb-icon-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.spb-upload-btn:hover,
.spb-clear-btn:hover,
.spb-ghost-btn:hover,
.spb-add-step-btn:hover,
.spb-icon-btn:hover,
.spb-delete-btn:hover {
  background: #f5f5f5;
}

.spb-hidden-upload {
  display: none;
}

.spb-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.spb-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.spb-field__label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.spb-input,
.spb-textarea,
.spb-select {
  width: 100%;
  border: 1px solid #dde2e6;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  box-sizing: border-box;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.spb-input,
.spb-select {
  height: 38px;
  padding: 0 12px;
}

.spb-textarea {
  min-height: 76px;
  padding: 10px 12px;
  resize: vertical;
  line-height: 1.5;
  font-family: inherit;
}

.spb-input:focus,
.spb-textarea:focus,
.spb-select:focus {
  border-color: #4384f5;
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.14);
}

.spb-step-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.spb-step-card__head,
.spb-field-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.spb-step-card__head {
  margin-bottom: 16px;
}

.spb-step-card__eyebrow {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.spb-step-card__title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.spb-step-card__actions,
.spb-field-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.spb-field-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}

.spb-field-card {
  border: 1px solid #eef2f7;
  border-radius: 10px;
  background: #fbfcfe;
  padding: 14px;
}

.spb-field-card__title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.spb-required-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #374151;
}

.spb-choice-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.spb-choice-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spb-input--choice {
  flex: 1;
}

.spb-add-field-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}

.spb-add-step-btn {
  align-self: flex-start;
}

.spb-empty-state {
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  padding: 14px;
  font-size: 13px;
  color: #64748b;
  background: #f8fafc;
}

.spb-delete-btn {
  color: #b42318;
}

.spb-delete-btn:disabled,
.spb-icon-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (max-width: 960px) {
  .spb-grid {
    grid-template-columns: 1fr;
  }

  .spb-hero-upload,
  .spb-add-field-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
